import { BALANCE_BUCKETS, computeMuscleLoad, ptSessionsToGymPayloads, type GymExercisePayload } from "@medassist/shared";
import { BarbellIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

const BUCKET_ORDER = Object.keys(BALANCE_BUCKETS);
const BUCKET_LABEL: Record<string, string> = { push: "Push", pull: "Pull", legs: "Legs", core: "Core", cardio: "Cardio" };

/** Min session count another bucket must reach before a zero-load bucket is
 *  called out — mirrors computeBalanceCallouts's threshold (D-GYM-4) without
 *  the extra fetch of the user's own/catalog exercises this small widget skips. */
const NEGLECT_MIN_OTHER_LOAD = 3;

export function TrainerWidget({ events, patient, onNavigate }: WidgetProps) {
  const tz = patient.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const gymPayloads = events
    .filter((e) => e.eventType === "gym_exercise" && !e.deleted)
    .map((e) => e.payload as unknown as GymExercisePayload);
  // PT sessions count 1:1 with gym logs on the Balance page (D-PTM-4); keep this widget
  // consistent with the full Balance tab rather than showing a different number.
  const ptPayloads = ptSessionsToGymPayloads(
    events.filter((e) => e.eventType === "pt_program"),
    events.filter((e) => e.eventType === "pt_session"),
    tz,
  );
  const load = computeMuscleLoad([...gymPayloads, ...ptPayloads], 7);
  const maxBucket = Math.max(1, ...Object.values(load.bucket));
  const neglected = BUCKET_ORDER.find(
    (b) => load.bucket[b] === 0 && Object.entries(load.bucket).some(([k, v]) => k !== b && v >= NEGLECT_MIN_OTHER_LOAD)
  );

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <BarbellIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Trainer</h2>
        <button onClick={() => onNavigate("trainer")} className="ml-auto text-xs text-(--accent) hover:underline">
          View
        </button>
      </div>
      <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">
        {load.sessionCount} session{load.sessionCount === 1 ? "" : "s"} this week
      </p>
      <div className="mb-2 flex items-end gap-1.5">
        {BUCKET_ORDER.map((b) => {
          const v = load.bucket[b] ?? 0;
          const pct = v > 0 ? Math.max(12, Math.min(100, (v / maxBucket) * 100)) : 0;
          return (
            <div key={b} className="flex flex-1 flex-col items-center gap-1">
              <div className="flex h-8 w-full items-end rounded bg-gray-100 dark:bg-gray-800">
                <div className="w-full rounded bg-(--accent)" style={{ height: `${pct}%` }} />
              </div>
              <span className="text-[10px] text-gray-400 dark:text-gray-500">{BUCKET_LABEL[b]}</span>
            </div>
          );
        })}
      </div>
      {neglected ? (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Consider adding some {BUCKET_LABEL[neglected].toLowerCase()} work this week.
        </p>
      ) : load.sessionCount === 0 ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">Log a session to see your muscle balance.</p>
      ) : (
        <p className="text-xs text-gray-400 dark:text-gray-500">Training looks balanced this week.</p>
      )}
    </section>
  );
}
